import type { UserNotificationStatus } from 'dynamo-daos/build/models/misc/userNotificationRecord';
import {
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useSnapshot } from 'valtio';

import * as notificationStore from '@cartwhl/common/stores/notificationStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import type { NotificationSurface } from '@cartwhl/common/utils/notificationLocation';
import {
  formatOrgCreditsForDisplay,
  getOrgAvailableCredits,
  isOrgCreditsWorkspace,
} from '@cartwhl/common/utils/organizationWorkspace';
import Avatar from '@cartwhl/ui/atoms/Avatar';
import ChevronDropdownIcon from '@cartwhl/ui/icons/ChevronDropdownIcon';
import AppsMenu from '@cartwhl/ui/organisms/AppsMenu';
import NotificationsMenu from '@cartwhl/ui/organisms/NotificationsMenu';
import UserMenu, { AccountCreditsLabel } from '@cartwhl/ui/organisms/UserMenu';
import UserMenuPopUp, {
  USER_MENU_POP_UP_POINTER_OFFSET_PX,
} from '@cartwhl/ui/pop-ups/UserMenuPopUp';

const DEFAULT_POINTER_TIP_RIGHT_PX = 26;
const DEFAULT_POP_UP_TOP_PX = 48;

/** Matches notification `location` anchor for user-menu pop-ups (e.g. `apps:/dashboard#user-menu`). */
const USER_MENU_POP_UP_ANCHOR = '#user-menu';

export default function UserMenuWithCredits({
  middleContent,
  currentPathname,
  currentSurface,
}: {
  middleContent?: ReactNode;
  currentPathname?: string;
  currentSurface?: NotificationSurface;
}) {
  const { newPopUpsByLocation } = useSnapshot(notificationStore.state);
  const {
    loading: userLoading,
    userInfo,
    organizationInfo,
  } = useSnapshot(userStore.state);

  const popUpAnchorRef = useRef<HTMLDivElement>(null);
  const avatarAnchorRef = useRef<HTMLSpanElement>(null);
  const [pointerTipRightPx, setPointerTipRightPx] = useState(
    DEFAULT_POINTER_TIP_RIGHT_PX,
  );
  const [popUpTopPx, setPopUpTopPx] = useState(DEFAULT_POP_UP_TOP_PX);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const popUpNotification =
    currentPathname &&
    currentSurface &&
    !userLoading &&
    newPopUpsByLocation
      ? notificationStore.getPopUpForRoute(
          currentSurface,
          currentPathname,
          USER_MENU_POP_UP_ANCHOR,
        )
      : undefined;

  const showPopUp = Boolean(popUpNotification) && !userMenuOpen;

  const orgCreditsWorkspace = isOrgCreditsWorkspace(organizationInfo);
  const orgAvailableCredits = getOrgAvailableCredits(organizationInfo);

  const creditsLabel = orgCreditsWorkspace
    ? `${formatOrgCreditsForDisplay(orgAvailableCredits)} credits`
    : `${userInfo.spinsAvailable ?? 0} Spins`;

  const secondaryLabel =
    `${userInfo.firstName ?? ''} ${userInfo.lastName ?? ''}`.trim() ||
    userInfo.email;

  const updatePopUpAlignment = useCallback(() => {
    const avatar = avatarAnchorRef.current;
    const container = popUpAnchorRef.current;
    if (!avatar || !container) {
      return;
    }

    const avatarRect = avatar.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const avatarCenterX = avatarRect.left + avatarRect.width / 2;
    const insetFromRight = containerRect.right - avatarCenterX;
    const avatarBottomFromContainerTop = avatarRect.bottom - containerRect.top;

    setPointerTipRightPx(insetFromRight);
    setPopUpTopPx(
      avatarBottomFromContainerTop + USER_MENU_POP_UP_POINTER_OFFSET_PX,
    );
  }, []);

  useLayoutEffect(() => {
    if (!showPopUp) {
      return;
    }

    updatePopUpAlignment();

    const avatar = avatarAnchorRef.current;
    const container = popUpAnchorRef.current;
    if (!avatar || !container) {
      return;
    }

    const resizeObserver = new ResizeObserver(updatePopUpAlignment);
    resizeObserver.observe(avatar);
    resizeObserver.observe(container);

    window.addEventListener('resize', updatePopUpAlignment);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updatePopUpAlignment);
    };
  }, [showPopUp, updatePopUpAlignment]);

  const setPopUpNotificationStatus = useCallback(
    async (status: UserNotificationStatus) => {
      if (!popUpNotification) {
        return;
      }
      await notificationStore.setNotificationStatus(
        popUpNotification.notificationID,
        status,
      );
    },
    [popUpNotification],
  );

  return (
    <div className="flex shrink-0 items-center gap-6">
      <div className="flex items-center gap-2">
        <AppsMenu />
        <NotificationsMenu />
      </div>
      {middleContent}
      <div ref={popUpAnchorRef} className="relative shrink-0">
        {showPopUp && popUpNotification ? (
          <UserMenuPopUp
            key={popUpNotification.notificationID}
            notification={popUpNotification}
            onDismiss={() => void setPopUpNotificationStatus('unread')}
            onLinkActivate={() => setPopUpNotificationStatus('read')}
            pointerTipRightPx={pointerTipRightPx}
            popUpTopPx={popUpTopPx}
          />
        ) : null}
        <UserMenu
          onOpenChange={setUserMenuOpen}
          trigger={
            <button
              type="button"
              className="outline-hidden hover:bg-gray-15 data-[state=open]:bg-gray-15 hover:dark:bg-gray-75 data-[state=open]:dark:bg-gray-90 flex shrink-0 cursor-pointer items-center gap-2 rounded-lg border-0 bg-transparent px-2 py-1 transition-colors sm:gap-2 sm:px-2.5 sm:py-1.5"
              aria-label="Account menu"
            >
              {!userLoading ? (
                <AccountCreditsLabel
                  creditsLabel={creditsLabel}
                  secondaryLabel={secondaryLabel}
                  className="pr-0.5"
                />
              ) : null}
              <span
                ref={avatarAnchorRef}
                className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-visible pr-0.5"
              >
                <Avatar userInfo={userInfo} className="relative">
                  <span className="dark:bg-gray-90 absolute -bottom-0.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-white">
                    <ChevronDropdownIcon className="text-primary h-3 w-3 shrink-0" />
                  </span>
                </Avatar>
              </span>
            </button>
          }
        />
      </div>
    </div>
  );
}
